import { Navigate, useParams } from "react-router-dom";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import usePageTitle from "@/hooks/usePageTitle";
import { FacilityGovernanceTable } from "@/pages/facilities/[facilityId]/people/governance/_FacilityGovernanceTable";
import { useSectionOperationsAccess } from "./useSectionOperationsAccess";

export default function SectionOperationsGovernancePage() {
  const { sectionId } = useParams();
  const {
    ready,
    sectionOfficeFacilityId,
    sectionNavError,
    canViewFacilities,
    showCommittees,
    showSectionOfficeFacilityTabs,
    governanceEnabled
  } = useSectionOperationsAccess(sectionId);

  usePageTitle(`/sections/${sectionId}/operations/governance`, "Sections | Operations | Governance");

  if (!sectionId || !ready) {
    return null;
  }

  if (!governanceEnabled) {
    return (
      <Navigate
        to={
          showCommittees
            ? `/sections/${sectionId}/operations/committees`
            : showSectionOfficeFacilityTabs
              ? `/sections/${sectionId}/operations/employees`
              : `/sections/${sectionId}/jobs`
        }
        replace
      />
    );
  }

  if (!canViewFacilities) {
    return (
      <Navigate
        to={showCommittees ? `/sections/${sectionId}/operations/committees` : `/sections/${sectionId}/jobs`}
        replace
      />
    );
  }

  if (sectionNavError) {
    return (
      <div className="w-full" data-testid="section-operations-governance">
        <ErrorBoundary
          variant="inline"
          title="Failed to Load Section Office"
          message="We couldn't load this section's office facility. Please try again or contact tech support."
          className="mt-12"
        />
      </div>
    );
  }

  if (!sectionOfficeFacilityId) {
    return (
      <div className="w-full" data-testid="section-operations-governance">
        <EmptyState
          iconName="account_balance"
          title="No Section Office Facility"
          caption="Governance for this section is managed on its section office facility. This section does not have one assigned."
        />
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="section-operations-governance">
      <FacilityGovernanceTable facilityId={sectionOfficeFacilityId} />
    </div>
  );
}
